import { useEffect, useState } from 'react';
import type { PagedResult, PrintRunRecord } from '../types/index';
import type { FieldSortCriterion } from '../utils/tableSort';
import { getPrintRunsTotals } from './usePrintRuns';

export default function usePrintRunsPageResults({ organizationId, page, pageSize, filters, sort }: {
  organizationId: number | string | null;
  page: number;
  pageSize: number;
  filters: { status: string; searchTerm: string };
  sort: FieldSortCriterion;
}) {
  const [state, setState] = useState<{
    organizationId: number | string | null;
    results: PagedResult<PrintRunRecord>;
    loading: boolean;
  }>({ organizationId, results: { page, pageSize, total: 0, data: [] }, loading: Boolean(organizationId) });

  useEffect(() => {
    let current = true;
    setState({ organizationId, results: { page, pageSize, total: 0, data: [] }, loading: Boolean(organizationId) });
    if (!organizationId) return () => { current = false; };

    getPrintRunsTotals({
      organizationId,
      page,
      pageSize,
      searchTerm: filters.searchTerm || null,
      status: filters.status === 'all' ? null : filters.status,
      sortField: sort?.fieldName ?? null,
      sortAscending: sort?.isAscending ?? null,
    }).then((results) => {
      if (current) setState({ organizationId, results: { ...results, page, pageSize }, loading: false });
    }).catch(() => {
      if (current) setState((value) => ({ ...value, loading: false }));
    });

    return () => { current = false; };
  }, [filters, organizationId, page, pageSize, sort?.fieldName, sort?.isAscending]);

  return state.organizationId === organizationId
    ? { results: state.results, loading: state.loading }
    : { results: { page: 0, pageSize, total: 0, data: [] }, loading: Boolean(organizationId) };
}
